import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Search, X, Star } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/ui/Icon';
import { usePermission } from '@/hooks/usePermission';
import { useFavourites } from '@/hooks/useSidebarFavorite';
import { NAV_SECTIONS, type NavItem } from '@/constants/navigation';

/** A searchable leaf (any nav item with a route), carrying its parent group label for context. */
/**
 * The selected menu item. Neutral, like the header bar it sits under, rather than a filled blue: a
 * light wash and dark text, with a thin orange edge (the accent used across the page headers) so it
 * still reads as "you are here". One string for all four places a selected item can appear.
 */
const ACTIVE_ITEM =
  'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-semibold shadow-[inset_2px_0_0_0_#f97316]';

interface FlatNavLeaf {
  item: NavItem;
  parentLabel?: string;
}

// myPOS ONE Dimensional Cube mark — dark variant (right face = Slate for sidebar visibility)
// ViewBox cropped tightly to cube geometry so mark fills the rendered size.
const SidebarCube = ({ size = 28 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="73 65 95 108" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
    <polygon points="120,72.1 160.87,95.7 120,119.3 79.13,95.7" fill="#3B82F6" />
    <polygon points="78.52,96.76 119.40,120.35 119.40,167.54 78.52,143.95" fill="#0891B2" />
    <polygon points="161.49,96.76 161.49,143.95 120.61,167.54 120.61,120.35" fill="#334155" />
    <polygon points="111.74,106.15 134.58,119.51 111.74,132.39" fill="#FFFFFF" />
  </svg>
);

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

interface NavGroupProps {
  item: NavItem;
  collapsed: boolean;
  expandedKeys: Set<string>;
  onToggleExpand: (key: string) => void;
}

const NavGroup = ({ item, collapsed, expandedKeys, onToggleExpand }: NavGroupProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermission();

  const visibleChildren = item.children?.filter(
    (child) => !child.permission || hasPermission(child.permission)
  ) ?? [];

  if (item.children && visibleChildren.length === 0) return null;

  const isExpanded = expandedKeys.has(item.key);
  const isChildActive = visibleChildren.some(
    (c) => c.path && (location.pathname === c.path || location.pathname.startsWith(c.path + '/'))
  );
  const isActive = item.path
    ? location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))
    : isChildActive;

  if (!item.children) {
    // Leaf item
    return (
      <li>
        <button
          onClick={() => item.path && navigate(item.path)}
          title={item.label}
          className={cn(
            'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
            isActive
              ? ACTIVE_ITEM
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
          )}
        >
          <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">
            <Icon name={item.icon} size={17} />
          </span>
          {!collapsed && <span className="truncate min-w-0 flex-1 text-left">{item.label}</span>}
        </button>
      </li>
    );
  }

  // Group with children
  return (
    <li>
      <button
        onClick={() => !collapsed && onToggleExpand(item.key)}
        title={item.label}
        className={cn(
          'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          isActive && !isExpanded
            ? 'bg-slate-100 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 font-medium'
            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
        )}
      >
        <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">
          <Icon name={item.icon} size={17} />
        </span>
        {!collapsed && (
          <>
            <span
              className={cn(
                'min-w-0 flex-1 text-left',
                isExpanded ? 'whitespace-normal break-words leading-tight' : 'truncate'
              )}
            >
              {item.label}
            </span>
            {isExpanded ? (
              <ChevronDown size={14} className="flex-shrink-0 opacity-60 ml-1" />
            ) : (
              <ChevronRight size={14} className="flex-shrink-0 opacity-60 ml-1" />
            )}
          </>
        )}
      </button>

      {/* Sub-items */}
      {!collapsed && isExpanded && (
        <ul className="mt-0.5 ml-3 pl-2.5 border-l border-slate-200 dark:border-slate-800/80 space-y-0.5 animate-fade-in">
          {visibleChildren.map((child) => {
            const childActive =
              child.path &&
              (location.pathname === child.path || location.pathname.startsWith(child.path + '/'));

            return (
              <li key={child.key}>
                <button
                  onClick={() => child.path && navigate(child.path)}
                  title={child.label}
                  className={cn(
                    'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-all',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                    childActive
                      ? ACTIVE_ITEM
                      : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
                  )}
                >
                  <Icon name={child.icon} size={14} className="flex-shrink-0" />
                  <span className="truncate min-w-0 flex-1 text-left">{child.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

export const Sidebar = ({ open, onToggle }: SidebarProps) => {
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set(['gl']));
  const [query, setQuery] = useState('');

  const toggleExpand = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // A group is shown when the user can reach anything inside it.
  //
  // It used to be gated on the group's own permission, which is just one of its children's --
  // Banking & Cash carries VIEW_BNK_TRANSFER. Someone entitled only to Petty Cash therefore lost
  // the entire group, including the four screens they did hold rights to, with no way in but a
  // typed URL. The group permission is kept as a shortcut for a group that has no children.
  const canReach = (s: NavItem) =>
    s.children?.length
      ? s.children.some((c) => !c.permission || hasPermission(c.permission))
      : !s.permission || hasPermission(s.permission);

  const visibleSections = NAV_SECTIONS.filter(canReach);

  // Flatten every routable nav item (top-level + children) into a searchable list,
  // respecting both the group's and the item's permission (parity with the tree).
  const flatLeaves = useMemo<FlatNavLeaf[]>(() => {
    const out: FlatNavLeaf[] = [];
    for (const section of NAV_SECTIONS) {
      // Same rule as the tree: judge the section by what is reachable inside it, or the search
      // box would keep hiding screens the menu now shows.
      if (!canReach(section)) continue;
      if (section.path) out.push({ item: section });
      for (const child of section.children ?? []) {
        if (child.permission && !hasPermission(child.permission)) continue;
        if (child.path) out.push({ item: child, parentLabel: section.label });
      }
    }
    return out;
  }, [hasPermission]);

  // Favourites (client-side, myPOS ONE parity) — resolve stored route paths to their nav leaf so
  // we can show the label + icon; permission-filtered via flatLeaves so a revoked screen drops out.
  const favPaths = useFavourites();
  const favItems = useMemo<NavItem[]>(
    () =>
      favPaths
        .map((p) => flatLeaves.find((l) => l.item.path === p)?.item)
        .filter((x): x is NavItem => !!x),
    [favPaths, flatLeaves]
  );

  const q = query.trim().toLowerCase();
  const searchResults = useMemo<FlatNavLeaf[]>(() => {
    if (!q) return [];
    return flatLeaves.filter(
      ({ item, parentLabel }) =>
        item.label.toLowerCase().includes(q) ||
        (parentLabel?.toLowerCase().includes(q) ?? false)
    );
  }, [q, flatLeaves]);

  const goTo = useCallback(
    (path?: string) => {
      if (path) {
        navigate(path);
        setQuery('');
      }
    },
    [navigate]
  );

  return (
    <>
      {/* Backdrop for mobile */}
      {open && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        style={{ width: open ? 240 : 60 }}
        className={cn(
          'fixed top-0 left-0 h-screen z-30 flex flex-col',
          'bg-white dark:bg-slate-900 border-r border-slate-200/90 dark:border-slate-800',
          'transition-[width] duration-200 ease-in-out overflow-hidden'
        )}
      >
        {/* Logo / Brand — myPOS ONE ecosystem mark */}
        <div
          className={cn(
            'flex items-center h-14 flex-shrink-0 border-b border-slate-200/90 dark:border-slate-800',
            open ? 'px-3 gap-2.5' : 'justify-center'
          )}
        >
          <SidebarCube size={open ? 36 : 32} />
          {open && (
            <div className="animate-fade-in overflow-hidden leading-none">
              {/* myLedger ONE — Sora ExtraBold, ONE in Signal Blue (brand spec) */}
              <span
                className="font-extrabold text-[13px] whitespace-nowrap leading-tight text-slate-900 dark:text-[#E6EDF7]"
                style={{ letterSpacing: '-0.01em', fontFamily: 'Sora, Inter, sans-serif' }}
              >
                myLedger <span style={{ color: '#3B82F6' }}>ONE</span>
              </span>
              <span
                className="block text-[9.5px] font-semibold whitespace-nowrap uppercase mt-1 text-slate-500 dark:text-slate-300"
                style={{ letterSpacing: '0.08em', fontFamily: 'Sora, Inter, sans-serif' }}
              >
                General Ledger
              </span>
            </div>
          )}
        </div>

        {/* Menu search */}
        {open && (
          <div className="flex-shrink-0 px-2 pt-3">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchResults.length > 0) goTo(searchResults[0].item.path);
                  else if (e.key === 'Escape') setQuery('');
                }}
                placeholder="Search menu..."
                aria-label="Search menu"
                className={cn(
                  'w-full rounded-lg bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/60',
                  'pl-8 pr-7 py-2 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
                )}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-100"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Nav items — filtered flat list while searching, else the tree */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {open && q ? (
            searchResults.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-400">No menu matches “{query}”.</p>
            ) : (
              <ul className="space-y-0.5">
                {searchResults.map(({ item, parentLabel }) => {
                  const active =
                    item.path &&
                    (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
                  return (
                    <li key={item.key}>
                      <button
                        onClick={() => goTo(item.path)}
                        title={item.label}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                          active
                            ? ACTIVE_ITEM
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
                        )}
                      >
                        <Icon name={item.icon} size={16} className="flex-shrink-0" />
                        <span className="flex-1 min-w-0 text-left">
                          <span className="block truncate">{item.label}</span>
                          {parentLabel && (
                            <span className="block truncate text-[10px] text-slate-400 dark:text-slate-500 font-normal">{parentLabel}</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            <>
              {/* Pinned favourites — toggled from the toolbar star, stored per-browser */}
              {favItems.length > 0 && (
                <div className="mb-2">
                  {open && (
                    <p className="px-3 pb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400/80">
                      <Star size={11} className="fill-amber-400 text-amber-400" />
                      Favourites
                    </p>
                  )}
                  <ul className="space-y-0.5">
                    {favItems.map((item) => {
                      const active =
                        item.path &&
                        (location.pathname === item.path ||
                          location.pathname.startsWith(item.path + '/'));
                      return (
                        <li key={`fav-${item.key}`}>
                          <button
                            onClick={() => goTo(item.path)}
                            title={item.label}
                            className={cn(
                              'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                              !open && 'justify-center',
                              active
                                ? ACTIVE_ITEM
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
                            )}
                          >
                            <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">
                              <Icon name={item.icon} size={17} />
                            </span>
                            {open && <span className="truncate min-w-0 flex-1 text-left">{item.label}</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-2 border-t border-slate-200/90 dark:border-slate-800" />
                </div>
              )}

              <ul className="space-y-0.5">
                {visibleSections.map((item) => (
                  <NavGroup
                    key={item.key}
                    item={item}
                    collapsed={!open}
                    expandedKeys={expandedKeys}
                    onToggleExpand={toggleExpand}
                  />
                ))}
              </ul>
            </>
          )}
        </nav>

        {/* Collapse toggle at bottom */}
        <div className="flex-shrink-0 border-t border-slate-200/90 dark:border-slate-800 p-2">
          <button
            onClick={onToggle}
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-slate-500 dark:text-slate-400',
              'hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors',
              !open && 'justify-center'
            )}
          >
            <Icon name={open ? 'PanelLeftClose' : 'PanelLeftOpen'} size={16} />
            {open && <span>Collapse</span>}
          </button>
        </div>
      </aside>
    </>
  );
};
